import React, { forwardRef } from 'react';

function cn(...classes) {
  return classes.filter(Boolean).join(' ');
}

const VARIANTS = {
  default: 'bg-white border border-gray-200/80 shadow-sm ring-1 ring-black/[0.04]',
  outlined: 'bg-white border-2 border-gray-200',
  elevated: 'bg-white shadow-xl shadow-gray-300/50 ring-1 ring-black/[0.05]',
  filled: 'bg-gradient-to-b from-gray-50 to-white border border-gray-200/70',
};

const Card = forwardRef(function Card(
  {
    children,
    variant = 'default',
    className,
    style,
    onClick,
    hoverable = false,
    ...rest
  },
  ref
) {
  const hoverableClasses = hoverable
    ? 'hover:-translate-y-0.5 hover:shadow-lg hover:shadow-gray-200/70 transition-all duration-200 cursor-pointer'
    : '';

  const cardClasses = cn(
    'rounded-xl overflow-hidden',
    VARIANTS[variant],
    hoverableClasses,
    className
  );

  return (
    <div
      ref={ref}
      className={cardClasses}
      style={style}
      onClick={onClick}
      {...rest}
    >
      {children}
    </div>
  );
});

Card.displayName = 'Card';

const CardHeader = forwardRef(function CardHeader(
  {
    children,
    className,
    style,
    ...rest
  },
  ref
) {
  return (
    <div
      ref={ref}
      className={cn(
        'flex flex-col space-y-1.5 px-6 py-5 border-b border-gray-100 bg-gradient-to-b from-gray-50/60 to-transparent',
        className
      )}
      style={style}
      {...rest}
    >
      {children}
    </div>
  );
});

CardHeader.displayName = 'CardHeader';

const CardTitle = forwardRef(function CardTitle(
  {
    children,
    as: Component = 'h3',
    className,
    style,
    ...rest
  },
  ref
) {
  return (
    <Component
      ref={ref}
      className={cn('text-base font-semibold leading-snug tracking-tight text-gray-900', className)}
      style={style}
      {...rest}
    >
      {children}
    </Component>
  );
});

CardTitle.displayName = 'CardTitle';

const CardDescription = forwardRef(function CardDescription(
  {
    children,
    className,
    style,
    ...rest
  },
  ref
) {
  return (
    <p
      ref={ref}
      className={cn('text-sm text-gray-500 leading-relaxed', className)}
      style={style}
      {...rest}
    >
      {children}
    </p>
  );
});

CardDescription.displayName = 'CardDescription';

const CardContent = forwardRef(function CardContent(
  {
    children,
    className,
    style,
    ...rest
  },
  ref
) {
  return (
    <div
      ref={ref}
      className={cn('p-6', className)}
      style={style}
      {...rest}
    >
      {children}
    </div>
  );
});

CardContent.displayName = 'CardContent';

const CardFooter = forwardRef(function CardFooter(
  {
    children,
    className,
    style,
    ...rest
  },
  ref
) {
  return (
    <div
      ref={ref}
      className={cn(
        'flex items-center justify-end gap-2 px-6 py-4 border-t border-gray-100 bg-gradient-to-b from-gray-50/40 to-transparent',
        className
      )}
      style={style}
      {...rest}
    >
      {children}
    </div>
  );
});

CardFooter.displayName = 'CardFooter';

export { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter };
export default Card;
